import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { Mailbox, Bell, Info } from 'lucide-react';
import api from '../../lib/api';

const TONE = {
  degrading: { bg: 'bg-rose-50 border-rose-200', text: 'text-rose-700', icon: 'text-rose-500',
               compactBg: 'bg-rose-50 border-rose-200 text-rose-700',
               compactDot: 'bg-rose-500' },
  improving: { bg: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-700', icon: 'text-emerald-500',
               compactBg: 'bg-emerald-50 border-emerald-200 text-emerald-700',
               compactDot: 'bg-emerald-500' },
  stable:    { bg: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-700', icon: 'text-emerald-500',
               compactBg: 'bg-emerald-50 border-emerald-200 text-emerald-700',
               compactDot: 'bg-emerald-500' },
  quiet:     { bg: 'bg-slate-50 border-slate-200',     text: 'text-slate-600',  icon: 'text-slate-400',
               compactBg: 'bg-slate-100 border-slate-200 text-slate-700',
               compactDot: 'bg-slate-400' },
};

// Status-specific remediation hints. Written for non-technical execs —
// surfaces what the indicator MEANS and what they should DO next.
const HINTS = {
  degrading: {
    headline: 'Email deliverability is degrading',
    body: "Recent recipients have been bouncing or marking us as spam. Review the suppression list and check your sender domain reputation before scheduling more digests or 'Send to CFO' emails.",
  },
  improving: {
    headline: 'Email deliverability is improving',
    body: 'Bounces are down week-over-week. Sender reputation is recovering — safe to resume normal sending cadence.',
  },
  stable: {
    headline: 'Email deliverability is healthy',
    body: 'Recent digests and prospectus emails are landing in inboxes. No action required.',
  },
  quiet: {
    headline: 'No email activity in the last 7 days',
    body: "Either nothing has been sent recently, or sends are succeeding without any bounces. If you expected activity, send a test from the prospectus 'Send to CFO' flow.",
  },
};

const _hintFor = (health) => {
  const h = HINTS[health.status] || HINTS.stable;
  if (health.last_alerted_at) {
    return {
      headline: h.headline,
      body: `${h.body} Last automated alert fired at ${new Date(health.last_alerted_at).toLocaleString()}.`,
    };
  }
  return h;
};

/**
 * EmailChannelHealthTile
 * ----------------------
 * Self-contained dashboard widget. Reads the sanitised
 * `/admin/email-channel-health` endpoint (org_admin or super_admin) and
 * renders an at-a-glance email-deliverability indicator.
 *
 *   Default mode: full Card tile (used on the Admin Dashboard).
 *   compact=true: pill-style chip designed for dark hero headers
 *                 (used on Seertech landing).
 *
 * Both modes hover-tooltip a status-specific remediation hint so
 * non-technical execs see what the indicator MEANS and what they
 * should DO next, rather than a mystery widget.
 *
 * Click-through to `/admin/email-suppressions` is enabled only for
 * super_admins so org_admins don't get a 403 redirect. Returns null
 * when the endpoint 403s or fails — surfaces simply hide rather than
 * showing a broken state.
 */
const EmailChannelHealthTile = ({ isSuperAdmin = false, compact = false }) => {
  const navigate = useNavigate();
  const [health, setHealth] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/admin/email-channel-health');
        if (!cancelled) setHealth(data);
      } catch {
        if (!cancelled) setHealth(null);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!health) return null;

  const tone = TONE[health.status] || { bg: '', text: 'text-slate-700', icon: 'text-slate-400',
                                         compactBg: 'bg-slate-100 border-slate-200 text-slate-700',
                                         compactDot: 'bg-slate-400' };
  const hint = _hintFor(health);

  // Tooltip body shared by both compact + full modes.
  const tooltipBody = (
    <div className="max-w-xs space-y-1" data-testid="admin-email-channel-health-tooltip">
      <p className="text-xs font-bold">{hint.headline}</p>
      <p className="text-[11px] leading-relaxed text-slate-200">{hint.body}</p>
      {isSuperAdmin && (
        <p className="text-[10px] text-teal-300 mt-1">Click to open suppression admin →</p>
      )}
    </div>
  );

  // "What IS this thing?" click-popover — separate from the hover
  // tooltip (which is status-specific). Answers the meta question
  // first-time stakeholders actually ask: why does my landing page
  // show me an email-deliverability indicator?
  const whatIsThisPopover = (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className="p-0.5 rounded-full hover:bg-slate-200 transition-colors flex-shrink-0"
          aria-label="What is the email channel indicator?"
          data-testid="admin-email-channel-health-info"
        >
          <Info className="w-3 h-3 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="end"
        sideOffset={8}
        collisionPadding={340}
        className="w-80 text-xs leading-relaxed text-slate-700"
        style={{ zIndex: 80 }}
        data-testid="admin-email-channel-health-info-content"
      >
        <div className="font-semibold text-slate-900 text-sm mb-1">Email Channel health</div>
        <p className="text-slate-600">
          A live indicator of your transactional email plumbing — Resend
          delivery rate, sender-domain DKIM/SPF reputation, and rolling
          bounce/complaint counts. <strong>Green</strong> means the
          stakeholder share emails ("Send report card to CFO", weekly
          digests, re-engage nudges) will land in inboxes; <strong>amber/red</strong>
          means recent sends have been bouncing or marked as spam and you
          should pause large-volume sends until reputation recovers.
        </p>
        {isSuperAdmin && (
          <p className="text-slate-500 mt-2 text-[11px] italic">
            Click the pill itself to open the Suppression Admin and inspect
            individual bounces/complaints.
          </p>
        )}
      </PopoverContent>
    </Popover>
  );

  if (compact) {
    const pill = (
      <span
        className={`inline-flex items-center gap-2 ${tone.compactBg} border rounded-full px-3 py-1.5 text-xs font-medium ${isSuperAdmin ? 'cursor-pointer hover:opacity-90 transition' : 'cursor-help'}`}
        data-testid="admin-email-channel-health-compact"
      >
        {health.last_alerted_at ? (
          <Bell className="w-3 h-3 text-rose-500" />
        ) : (
          <span className={`w-2 h-2 rounded-full ${tone.compactDot}`} />
        )}
        <span className="text-[10px] uppercase tracking-wider opacity-70">Email channel</span>
        <span className="font-bold">{health.label}</span>
        {whatIsThisPopover}
      </span>
    );
    const wrapped = isSuperAdmin ? (
      // NOTE: must NOT be a <button>. The pill contains a <PopoverTrigger>
      // which renders a <button>; nesting buttons triggers a React
      // hydration warning. A focusable span carries the click affordance
      // without breaking accessibility.
      <span
        role="button"
        tabIndex={0}
        onClick={() => navigate('/admin/email-suppressions')}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            navigate('/admin/email-suppressions');
          }
        }}
        className="inline-flex"
      >
        {pill}
      </span>
    ) : pill;
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>{wrapped}</TooltipTrigger>
          <TooltipContent side="bottom">{tooltipBody}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  const tile = (
    <Card
      className={`${tone.bg} ${isSuperAdmin ? 'cursor-pointer hover:shadow-md transition' : 'cursor-help'}`}
      data-testid="admin-email-channel-health"
    >
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500 flex items-center gap-1">
              Email Channel
              {whatIsThisPopover}
            </p>
            <p className={`text-2xl font-bold ${tone.text}`}>{health.label}</p>
            {health.last_alerted_at ? (
              <p className="text-[10px] text-rose-600 mt-0.5">Alerted recently</p>
            ) : isSuperAdmin ? (
              <p className="text-[10px] text-slate-400 mt-0.5">View suppressions →</p>
            ) : null}
          </div>
          <Mailbox className={tone.icon} size={32} />
        </div>
      </CardContent>
    </Card>
  );

  const wrappedTile = isSuperAdmin ? (
    <div onClick={() => navigate('/admin/email-suppressions')} role="button" tabIndex={0}>
      {tile}
    </div>
  ) : tile;

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>{wrappedTile}</TooltipTrigger>
        <TooltipContent side="bottom">{tooltipBody}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default EmailChannelHealthTile;
